import React, { useRef, useState } from "react"
import { Button } from "antd"
import type { FormInstance } from "antd"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { ChevronLeftIcon } from "@heroicons/react/24/outline"
import { Button as CustomButton } from "components"
import { ewaAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import { useAppNotificationStore } from "stores"
import type { CreateShiftPlanBody, HardwareDevices } from "../../typings"
import CreateShiftPlanDetails, {
  toIsoDateString,
} from "./CreateShiftPlanDetails"
import type { DateInputValue } from "./DateInput"
import CreateShiftPlanConfiguration from "./CreateShiftPlanConfiguration"
import CreateShiftPlanPeriods from "./CreateShiftPlanPeriods"
import CreateShiftPlanTables from "./CreateShiftPlanTables"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import {
  bufferMinutes,
  invalidateShiftPlanQueries,
} from "../../utils/shiftTablesUtils"
import { ConfigFormValues } from "./SetupShiftPlans"

// ─── Constants ────────────────────────────────────────────────────────────────

const CREATE_STEPS = [
  "Shift plan details",
  "Configure shifts",
  "Assign employees",
  "Review & confirm",
] as const

const FAQ_DISMISSED_KEY = "create_shift_plan_faq_dismissed"

// ─── Types ────────────────────────────────────────────────────────────────────

interface CreateShiftPlanProps {
  onBack: () => void
  onComplete: () => void
  saveToDraft?: () => void
  workplaceId?: number
  isDrafting?: boolean
  onTrackProgress?: (updates: Record<string, boolean>) => void
  isTrackingProgress?: boolean
}

interface DetailsFormValues {
  shiftPlanName?: string
  dutyLocation?: string | number
  shiftType?: string
  startDate?: DateInputValue
  endDate?: DateInputValue
}

export interface CreatedShiftPlanSummary {
  id: number
  title: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveDutyLocationId(
  raw: string | number | undefined
): number | null {
  if (raw == null || raw === "workplace" || raw === "") return null
  const n = Number(raw)
  return Number.isNaN(n) ? null : n
}

function buildCreateShiftPlanBody(
  details: DetailsFormValues,
  config: CreateShiftPlanBody,
  workplaceId: number
): CreateShiftPlanBody {
  const dutyLocationId = resolveDutyLocationId(details.dutyLocation)

  const body = {
    title: details.shiftPlanName ?? "",
    type: details.shiftType === "anytime" ? "flexible" : "rostered",
    status: "published",
    workplaceId: String(workplaceId),
    ...(dutyLocationId != null && { dutyLocationId }),
    allowManualTimeEntry: !!config.allowManualTimeEntry || false,
    requireApprovalForManualTimeEntry:
      !!config.requireApprovalForManualTimeEntry || false,
    allowedClockInMinutesBeforeShift: Number(
      config.allowedClockInMinutesBeforeShift ?? 0
    ),
    allowOvertime: !!config.allowOvertime || false,
    addOvertimeToPayroll: !!config.addOvertimeToPayroll || false,
    linkHardwareDevice: !!config.linkHardwareDevice || false,
    hardwareDeviceTypes: config.hardwareDeviceTypes
      ? (config.hardwareDeviceTypes.map((d: string | HardwareDevices) =>
          typeof d === "string" ? d : d.deviceType
        ) as (string | HardwareDevices)[])
      : [],
    ...(config.hardwareSetupContactEmail
      ? { hardwareSetupContactEmail: String(config.hardwareSetupContactEmail) }
      : {}),
    allowGeoFencing: !!config.allowGeoFencing,
    geofencingOffsetMeters: Number(config.geofencingOffsetMeters ?? 0),
    useInPayrollCalculation: !!config.useInPayrollCalculation,
    allowSwapping: !!config.allowSwapping,
    requireApprovalForSwap: !!config.requireApprovalForSwap,
    allowLateClockIn: !!config.allowLateClockIn,
    allowedLateClockinInMinutes: Number(
      config.allowedLateClockinInMinutes ?? 0
    ),
    startDate: toIsoDateString(details.startDate),
    endDate: toIsoDateString(details.endDate),
    notificationCadence: String(config.notificationCadence ?? ""),
    notifiyMeBefore: !!config.notifiyMeBefore || false,
    smsNotification: !!config.smsNotification || false,
    useOvertimeRecord: !!config.useOvertimeRecord || false,
    notifyMeBeforeSmsSends: !!config.notifiyMeBefore || false,
  }
  return body as CreateShiftPlanBody
}

// ─── Component ────────────────────────────────────────────────────────────────

const CreateShiftPlan: React.FC<CreateShiftPlanProps> = ({
  onBack,
  onComplete,
  workplaceId,
  onTrackProgress,
  isTrackingProgress = false,
  saveToDraft,
  isDrafting,
}) => {
  const { toast } = useAppNotificationStore()
  const queryClient = useQueryClient()

  const [currentStep, setCurrentStep] = useState(0)
  const [savedDetailsValues, setSavedDetailsValues] =
    useState<DetailsFormValues>({})
  const [savedConfigValues, setSavedConfigValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const [createdShiftPlan, setCreatedShiftPlan] =
    useState<CreatedShiftPlanSummary | null>(null)
  const [hasShiftPeriods, setHasShiftPeriods] = useState(false)
  const [showFAQs, setShowFAQs] = useState(true)

  const detailsFormRef = useRef<FormInstance>(null)
  const configFormRef = useRef<FormInstance>(null)

  const shiftPlanName =
    createdShiftPlan?.title ?? savedDetailsValues.shiftPlanName ?? ""

  // ── Mutation ───────────────────────────────────────────────────────────────

  const { mutate: createShiftPlan, isLoading: isCreating } = useMutation({
    mutationFn: async (body: CreateShiftPlanBody) => {
      const res = await ewaAxiosInstance.post(
        "/v1/time-attendance/shift-plans",
        body
      )
      return res.data
    },
    onSuccess: (res) => {
      invalidateShiftPlanQueries(queryClient)
      toast.success("Shift plan created successfully")
      onTrackProgress?.({ shiftPermissionsConfigured: true })

      const data = res?.data
      if (data?.id && data?.title) {
        setCreatedShiftPlan({ id: data.id, title: data.title })
      }
      setCurrentStep(2)
    },
    onError: onQueryError,
  })

  const { mutate: updateShiftPlan, isLoading: isUpdating } = useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: number
      body: CreateShiftPlanBody
    }) => {
      const res = await ewaAxiosInstance.put(
        `/v1/time-attendance/shift-plans/${id}`,
        body
      )
      return res.data
    },
    onSuccess: (res) => {
      invalidateShiftPlanQueries(queryClient)
      toast.success("Shift plan updated successfully")
      onTrackProgress?.({ shiftPermissionsConfigured: true })

      const data = res?.data
      if (data?.id && data?.title) {
        setCreatedShiftPlan({ id: data.id, title: data.title })
      }
      setCurrentStep(2)
    },
    onError: onQueryError,
  })

  // ── Step handlers ──────────────────────────────────────────────────────────

  const handleNext = async () => {
    if (currentStep === 0) {
      try {
        await detailsFormRef.current?.validateFields()
        const details = detailsFormRef.current?.getFieldsValue() ?? {}
        setSavedDetailsValues({
          shiftPlanName: details.shiftPlanName,
          dutyLocation: details.dutyLocation,
          shiftType: details.shiftType,
          startDate: details.startDate,
          endDate: details.endDate,
        })
        onTrackProgress?.({ shiftPlanConfigured: true })
        setCurrentStep(1)
      } catch {
        // Validation errors shown inline by Ant Design
      }
      return
    }

    if (currentStep === 1) {
      try {
        await configFormRef.current?.validateFields()
        const config = configFormRef.current?.getFieldsValue()
        setSavedConfigValues(config)
        if (workplaceId == null) {
          toast.error("Missing workplace id")
          return
        }

        const body = buildCreateShiftPlanBody(
          savedDetailsValues,
          config,
          workplaceId
        )
        if (createdShiftPlan?.id) {
          updateShiftPlan({ id: createdShiftPlan.id, body })
        } else {
          createShiftPlan(body)
        }
      } catch {
        // Validation errors shown inline by Ant Design
      }
      return
    }

    if (currentStep === 2) {
      onTrackProgress?.({ shiftPeriodConfigured: true })
    }

    if (currentStep === 3) {
      onTrackProgress?.({ shiftEmployeesConfigured: true })
    }

    if (currentStep < CREATE_STEPS.length - 1) {
      setCurrentStep((s) => s + 1)
    } else {
      setSavedDetailsValues({})
      setSavedConfigValues(null)
      setCreatedShiftPlan(null)
      onComplete()
    }
  }

  const handleBack = () => {
    if (currentStep === 0) {
      onBack()
    } else {
      setCurrentStep((s) => s - 1)
    }
  }

  // ── Step renderer ──────────────────────────────────────────────────────────

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <CreateShiftPlanDetails
            formRef={detailsFormRef}
            workplaceId={workplaceId}
            initialValues={savedDetailsValues}
          />
        )
      case 1:
        return (
          <CreateShiftPlanConfiguration
            key={savedConfigValues ? "with-saved" : "default"}
            shiftPlanName={shiftPlanName}
            formRef={configFormRef}
            initialValues={savedConfigValues ?? undefined}
          />
        )
      case 2:
        return (
          <CreateShiftPlanPeriods
            shiftPlanName={shiftPlanName}
            shiftPlanId={createdShiftPlan?.id}
            onShiftPeriodsChange={(count) => setHasShiftPeriods(count > 0)}
          />
        )
      case 3:
        return (
          <CreateShiftPlanTables
            shiftPlanName={shiftPlanName}
            shiftType={
              (savedDetailsValues.shiftType as "rostered" | "anytime") ??
              "rostered"
            }
            shiftPlanId={createdShiftPlan?.id}
            dutyLocationId={
              resolveDutyLocationId(savedDetailsValues.dutyLocation) ??
              undefined
            }
            workplaceId={workplaceId}
          />
        )
      default:
        return null
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Header (static) */}
      <div className="flex flex-col gap-4 shrink-0">
        <Button
          type="default"
          className="self-start font-semibold px-4 py-2 h-auto rounded-lg border-2 border-[#E2E8F0] text-[#0F172B] flex items-center gap-2"
          icon={<ChevronLeftIcon className="w-4 h-4" />}
          onClick={handleBack}
        >
          Go back
        </Button>

        <div className="">
          <h5 className="text-2xl text-[#0F172B] font-bold">Shift plans</h5>
          <p className="text-[#64748B] text-sm">
            Choose how you want to schedule employees for this workplace. You
            can create multiple shift plans with different types later.
          </p>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-6 pt-6 pr-2">
        {renderStep()}
      </div>

      <div className="shrink-0 flex justify-end items-center gap-3 pt-4 border-t border-[#E2E8F0]">
        <Button
          type="default"
          className="font-semibold px-6 h-10 rounded-lg border-2 border-[#E2E8F0] text-[#0F172B]"
          onClick={saveToDraft}
          loading={isDrafting}
        >
          Save to drafts
        </Button>
        <Button
          type="primary"
          className="font-semibold px-6 h-10 rounded-lg bg-[#004AF5] text-white"
          onClick={handleNext}
          loading={
            (currentStep === 1 && (isCreating || isUpdating)) ||
            isTrackingProgress
          }
          disabled={
            (currentStep === 2 && !hasShiftPeriods) ||
            isCreating ||
            isUpdating ||
            isTrackingProgress ||
            isDrafting
          }
        >
          Next step
        </Button>
      </div>

      <ShiftPlanFAQModal
        open={showFAQs}
        onClose={() => setShowFAQs(false)}
      />
    </>
  )
}

export default CreateShiftPlan

// ─── FAQ Modal ────────────────────────────────────────────────────────────────

const ShiftPlanFAQModal: React.FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => (
  <CustomModal
    rootClassName="ta-scope"
    open={open}
    width={800}
    title="Shift plans"
    onCancel={onClose}
    footer={
      <CustomButton
        color="primary"
        className="font-semibold w-full h-10"
        onClick={onClose}
      >
        Close
      </CustomButton>
    }
  >
    <div className="flex flex-col justify-center items-center gap-2 mb-4">
      <img
        src="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/2646725a-7fff-4056-b878-08b903e1821f.png"
        alt="Shift plans"
        className="w-[200px] h-[140px] object-contain"
        width={200}
        height={140}
      />
      <p className="font-bold text-lg text-slate-900">Shift plans</p>
      <p className="text-sm text-slate-500">
        You're about to setup shifts, here are some useful definitions.
      </p>
    </div>

    <div className="grid grid-cols-2 gap-2">
      <ShiftFAQCard title="General Explanations">
        Think of this as a three-level system: Shift Plan (the big container) →
        Shift Table (a schedule table with its own start/end dates — table dates
        can't overlap) → Shift Period (individual time slots like "Morning" or
        "Night"). These three levels work together to control when and where
        people work.
      </ShiftFAQCard>
      <ShiftFAQCard title="Shift Plan">
        A shift plan is your master schedule for one location. It answers: Where
        are people working? When does this schedule run? And are people assigned
        to specific days (Rostered) or can they choose their own days
        (Flexible)? You can assign someone to multiple shift plans, but their
        actual shift times across those plans can't clash.
      </ShiftFAQCard>
      <ShiftFAQCard title="Shift Table">
        Think of this as the actual scheduling table inside your shift plan. It
        has its own start and end dates, so you can create different schedules
        for different weeks or months within the same overall plan. For example:
        "Week 1–4: Team schedule" and "Week 5–8: A different Team schedule," all
        under one shift plan. Each table applies to the same duty location as
        the parent plan.
      </ShiftFAQCard>
      <ShiftFAQCard title="Shift Period">
        This is a time block with a name — like "Morning Shift: 8 AM–5 PM." Each
        period needs a name, start time, and end time. The key rule: periods
        can't overlap. If you have a Morning shift ending at 5 PM, your Evening
        shift can't start until 5 PM or later.
      </ShiftFAQCard>
    </div>
  </CustomModal>
)

const ShiftFAQCard: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="border-2 border-[#E2E8F0] rounded-md p-4 flex flex-col gap-2">
    <p className="font-bold">{title}</p>
    <p className="text-slate-500">{children}</p>
  </div>
)
